import { useCallback } from 'react'

import { Pencil, Receipt, TrashBin } from '@gravity-ui/icons'
import { Button, cn, Table, Tooltip } from '@heroui/react'
import { useTranslation } from 'react-i18next'

import type { PaidReport } from '@/types/settlement'
import { formatPLN } from '@/utils/currency'
import { formatDate } from '@/utils/dates'

interface PaymentRowProps {
  id: string;
  date: string;
  amount: number;
  paidReports?: PaidReport[];
  onNavigateReport?: (id: string) => void;
  onEdit?: (id: string) => void;
  onDelete?: (id: string) => void;
}

interface PaidReportTagProps {
  report: PaidReport;
  onNavigate?: (id: string) => void;
}

function PaidReportTag({ report, onNavigate }: PaidReportTagProps) {
  const handleClick = useCallback(() => {
    onNavigate?.(report.ID)
  }, [report.ID, onNavigate])

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={!onNavigate}
      className={cn(
        "text-[10px] leading-none font-mono font-bold text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/30 px-1.5 py-1 rounded border border-emerald-100 dark:border-emerald-900/30 transition-all duration-200",
        onNavigate ? "hover:bg-emerald-100 dark:hover:bg-emerald-900/50 cursor-pointer active:scale-95" : "cursor-default"
      )}
      title={report.Name}
    >
      {report.Name}
    </button>
  )
}

export function PaymentRow({
  id,
  date,
  amount,
  paidReports,
  onNavigateReport,
  onEdit,
  onDelete,
}: PaymentRowProps) {
  const { t } = useTranslation()

  const handleEdit = useCallback(() => {
    if (onEdit) {
      onEdit(id)
    }
  }, [id, onEdit])

  const handleDelete = useCallback(() => {
    if (onDelete) {
      onDelete(id)
    }
  }, [id, onDelete])

  return (
    <Table.Row key={id}>
      <Table.Cell>
        <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 bg-emerald-50 text-emerald-600 dark:bg-emerald-950/30 dark:text-emerald-400">
          <Receipt className="size-4" />
        </div>
      </Table.Cell>
      <Table.Cell>
        <span className="font-medium">
          {formatDate(date)}
        </span>
      </Table.Cell>
      <Table.Cell>
        <div className="flex items-center flex-wrap gap-2">
          <span className="font-bold text-zinc-800 dark:text-zinc-200 shrink-0">
            {t('settlements.type_payment')}
          </span>
          {paidReports && paidReports.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {paidReports.map(rep => (
                <PaidReportTag
                  key={rep.ID}
                  report={rep}
                  onNavigate={onNavigateReport}
                />
              ))}
            </div>
          )}
        </div>
      </Table.Cell>
      <Table.Cell className="text-right font-black tabular-nums">
        <span className="text-emerald-600 dark:text-emerald-400">
          + {formatPLN(amount)} PLN
        </span>
      </Table.Cell>
      <Table.Cell>
        <div className="flex justify-end gap-2">
          <Tooltip delay={0}>
            <Button
              isIconOnly
              size="sm"
              variant="tertiary"
              onClick={handleEdit}
              aria-label={t('common.edit')}
            >
              <Pencil className="size-4 text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-200" />
            </Button>
            <Tooltip.Content>
              <p>{t('common.edit')}</p>
            </Tooltip.Content>
          </Tooltip>

          <Tooltip delay={0}>
            <Button
              isIconOnly
              size="sm"
              variant="danger-soft"
              onClick={handleDelete}
              aria-label={t('common.delete')}
            >
              <TrashBin className="size-4 text-rose-500 hover:text-rose-700 dark:hover:text-rose-400" />
            </Button>
            <Tooltip.Content>
              <p>{t('common.delete')}</p>
            </Tooltip.Content>
          </Tooltip>
        </div>
      </Table.Cell>
    </Table.Row>
  )
}
